import { HoursStatusComponent } from './../hours-status/hours-status.component';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { MENU_ITEMS } from './menu-items';
import { takeUntil, map } from 'rxjs/operators';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import {
  NbThemeService,
  NbSidebarService,
  NbMenuItem,
  NbMenuService,
  NbLayoutScrollService
} from '@nebular/theme';
import { LayoutService } from './../../services/layout.service';
import { Subject, Observable, fromEvent } from 'rxjs';
import { ShopService } from 'src/app/shop/shop.service';

@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss']
})
export class ShellComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('footerHours') footerHours: HoursStatusComponent;
  @ViewChild('mainHeaderRef', { read: ElementRef }) mainHeaderRef?: ElementRef;
  @ViewChild('navHeaderRef', { read: ElementRef }) navHeaderRef?: ElementRef;

  themes = [
    {
      value: 'default',
      name: 'Light'
    },
    {
      value: 'dark',
      name: 'Dark'
    }
  ];

  currentTheme = 'default';
  menu: NbMenuItem[] = MENU_ITEMS;
  userMenu: NbMenuItem[] = [{ title: 'Logout' }];
  claims: {
    [key: string]: any;
  } = { roles: [] };
  $destroy = new Subject<boolean>();
  $menuClickDestroy = new Subject<boolean>();

  clickObservable: Observable<Event>;

  clickX;
  clickY;
  lastMenuItemSelected: NbMenuItem;
  menuClickSubscription;

  isHeaderFixed = false;
  isShopEnabled = false;
  isExclusivesEnabled = false;
  isHandset = true;
  subheaderHeight = 0;
  mainHeaderHeight = 110;
  scrollbarWidth = 0;
  private resizeObserver?: ResizeObserver;
  private pinObserver?: IntersectionObserver;

  constructor(
    public themeService: NbThemeService,
    public sidebarService: NbSidebarService,
    public scrollService: NbLayoutScrollService,
    public authService: AuthService,
    public layoutService: LayoutService,
    private shopService: ShopService,
    public router: Router,
    private menuService: NbMenuService,
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: any
  ) {}

  async ngOnInit(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      this.clickObservable = fromEvent(document, 'click');
      this.layoutService.$isHandset.pipe(takeUntil(this.$destroy)).subscribe((isHandset) => {
        this.isHandset = isHandset;
      });
    }
    this.shopService
      .shopSettings$()
      .pipe(takeUntil(this.$destroy))
      .subscribe((settings) => {
        this.isShopEnabled = settings.isShopEnabled;
        this.isExclusivesEnabled = !!settings.isExclusivesEnabled;
        // clear to prevent multiple
        this.menu = this.menu.filter((m) => {
          return m.title !== 'Shop Online' && m.title !== 'Online Exclusives';
        });
        if (this.isShopEnabled) {
          this.menu.push({
            title: 'Shop Online',
            icon: 'shopping-cart-outline',
            link: '/shop',
            pathMatch: 'prefix'
          });
        }
        if (this.isExclusivesEnabled) {
          this.menu.push({
            title: 'Online Exclusives',
            icon: 'globe-outline',
            link: '/exclusives'
          });
        }
      });
    // Sidebar auto-collapse on scroll (pin trigger is handled by IntersectionObserver in ngAfterViewInit).
    this.scrollService
      .onScroll()
      .pipe(takeUntil(this.$destroy))
      .subscribe(() => {
        this.sidebarService.collapse('menu-sidebar');
      });

    this.menuService
      .onItemSelect()
      .pipe(takeUntil(this.$destroy))
      .subscribe(async () => {
        this.sidebarService.collapse('menu-sidebar');
      });
    this.menuService
      .onSubmenuToggle()
      .pipe(takeUntil(this.$destroy))
      .subscribe((select) => {
        this.lastMenuItemSelected = select.item;
        if (
          isPlatformBrowser(this.platformId) &&
          !this.menuClickSubscription &&
          this.clickObservable
        ) {
          this.menuClickSubscription = this.clickObservable
            .pipe(takeUntil(this.$menuClickDestroy))
            .subscribe((click: MouseEvent) => {
              if (!this.lastMenuItemSelected) {
                // clicked off
                //this.menuService.collapseAll();
                this.$menuClickDestroy.next(true);
                this.menuClickSubscription = undefined;
              }
              this.lastMenuItemSelected = undefined;
            });
        }
      });
    this.menuService
      .onItemClick()
      .pipe(map(({ item: { title } }) => title))
      .subscribe(async (title) => {
        switch (title) {
          case 'Logout':
            await this.logout();
            break;
          case 'Store Hours':
            // store hours clicked
            if (this.footerHours) {
              this.footerHours.open();
            }
            break;
          default:
            break;
        }
      });
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.measureMainHeader();
    this.measureSubheader();
    this.measureScrollbar();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.measureMainHeader();
        this.measureSubheader();
        this.measureScrollbar();
      });
      if (this.mainHeaderRef?.nativeElement) {
        this.resizeObserver.observe(this.mainHeaderRef.nativeElement);
      }
      if (this.navHeaderRef?.nativeElement) {
        this.resizeObserver.observe(this.navHeaderRef.nativeElement);
      }
    }
    this.setupPinObserver();
  }

  private setupPinObserver(): void {
    if (typeof IntersectionObserver === 'undefined' || !this.mainHeaderRef?.nativeElement) {
      return;
    }
    const mainHeaderEl = this.mainHeaderRef.nativeElement as HTMLElement;
    const scrollRoot = this.findScrollableAncestor(mainHeaderEl);
    // Fires when the main header transitions in/out of intersection with the scroll root.
    // When it fully scrolls out (above the viewport top), pin the sub-header.
    this.pinObserver = new IntersectionObserver(
      ([entry]) => {
        const next = !entry.isIntersecting;
        if (next === this.isHeaderFixed) {
          return;
        }
        // IntersectionObserver fires outside Angular's zone — re-enter so CD picks up the change.
        this.zone.run(() => {
          this.isHeaderFixed = next;
        });
      },
      {
        root: scrollRoot,
        threshold: 0
      }
    );
    this.pinObserver.observe(mainHeaderEl);
  }

  private findScrollableAncestor(el: HTMLElement): HTMLElement | null {
    let cur: HTMLElement | null = el.parentElement;
    while (cur) {
      const overflowY = getComputedStyle(cur).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return cur;
      }
      cur = cur.parentElement;
    }
    return null;
  }

  private measureMainHeader(): void {
    if (!this.mainHeaderRef?.nativeElement) {
      return;
    }
    this.mainHeaderHeight = (this.mainHeaderRef.nativeElement as HTMLElement).offsetHeight;
  }

  private measureSubheader(): void {
    if (this.isHeaderFixed || !this.navHeaderRef?.nativeElement) {
      return;
    }
    const el = this.navHeaderRef.nativeElement as HTMLElement;
    const marginBottom = parseFloat(getComputedStyle(el).marginBottom) || 0;
    this.subheaderHeight = el.offsetHeight + marginBottom;
  }

  private measureScrollbar(): void {
    if (!this.navHeaderRef?.nativeElement) {
      return;
    }
    const subheaderEl = this.navHeaderRef.nativeElement as HTMLElement;
    const scrollEl = this.findScrollableAncestor(subheaderEl);
    this.scrollbarWidth = scrollEl ? scrollEl.offsetWidth - scrollEl.clientWidth : 0;
    subheaderEl.style.setProperty('--app-nav-scrollbar-width', `${this.scrollbarWidth}px`);
  }

  async ngOnDestroy(): Promise<void> {
    this.resizeObserver?.disconnect();
    this.pinObserver?.disconnect();
    this.$destroy.next(true);
    this.$menuClickDestroy.next(true);
  }

  saveThemePreference(themeName: string): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('theme', themeName);
    }
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(false, 'menu-sidebar');
    this.layoutService.changeLayoutSize();

    return false;
  }

  async logout(): Promise<void> {
    await this.router.navigateByUrl('/home');
    await this.authService.logout();
  }
}
